'use client'

import { createContext, useCallback, useContext, useState } from 'react'

import type { ErrorCodes, ErrorType, ErrorsGroup } from '@src/utils/constants.util'
import {
  type JSendFailure,
  getJSendErrorAdditionalDetails,
  getJSendErrorCode,
  getJSendErrorMessage,
  getJSendErrorType,
} from '@src/utils/jsend.util'
import { log } from '@src/utils/log.util'

const initialState = {
  error: null as JSendFailure | Error | null,
  type: null as ErrorType | null,
  code: null as ErrorCodes | null,
  message: null as string | null,
  additionalDetails: null as any,
  reset: () => {},
  setError: (
    _value: JSendFailure | Error,
    _options?: { group?: ErrorsGroup; duration?: number }
  ) => {},
}

export const ErrorsContext = createContext(initialState)

export type ErrorsContextType = typeof initialState

export interface ErrorsProviderProps {
  children: React.ReactNode
  serverData?: null | {
    error: JSendFailure | Error | null
    type: ErrorType | null
    code: ErrorCodes | null
    message: string | null
    additionalDetails?: any
  }
}

/**
 * A provider that allows to set and reset errors for a whole page using Context.
 * @param children
 * @param serverData - Initial error data from the server.
 * Use getJSendErrorInfo to serialize the Error server to client.
 * @returns
 */
export const ErrorsProvider = ({ children, serverData }: ErrorsProviderProps) => {
  const [error, _setError] = useState(serverData?.error ?? null)
  const [type, setType] = useState(serverData?.type ?? null)
  const [code, setCode] = useState(serverData?.code ?? null)
  const [message, setMessage] = useState(serverData?.message ?? null)
  const [additionalDetails, setAdditionalDetails] = useState(serverData?.additionalDetails ?? null)
  const [timeout, setTimeout] = useState<number | null>(null)

  const reset = useCallback(() => {
    _setError(null)
    setType(null)
    setCode(null)
    setMessage(null)
    setTimeout(null)
  }, [])

  const setError = useCallback<ErrorsContextType['setError']>(
    (error, options) => {
      _setError(error)
      setType(getJSendErrorType(error, options?.group ?? null))
      setCode(getJSendErrorCode(error))
      setMessage(getJSendErrorMessage(error))
      setAdditionalDetails(getJSendErrorAdditionalDetails(error))

      if (options?.duration) {
        clearTimeout(timeout as number)
        setTimeout(window.setTimeout(reset, options.duration))
      }
    },
    [timeout, reset]
  )

  return (
    <ErrorsContext.Provider
      value={{ error, type, code, message, additionalDetails, setError, reset }}
    >
      {children}
    </ErrorsContext.Provider>
  )
}

export const useErrors = () => {
  try {
    return useContext(ErrorsContext)
  } catch (error) {
    log.error('useCheckoutErrors must be used within a CheckoutErrorsProvider')
    return initialState
  }
}
